import { countries } from "@redotech/locale/countries";
import { Currency, getCurrencySymbol } from "@redotech/money/currencies";
import { moneyFormat } from "@redotech/money/util";
import { IterableMap } from "@redotech/react-util/component";
import { useHandler } from "@redotech/react-util/hook";
import { useLoad } from "@redotech/react-util/load";
import { CurrencyConverterContext } from "@redotech/react-util/money";
import HelpIcon from "@redotech/redo-icons/help-circle.svg";
import {
  pricePerOrder as calcPricePerOrder,
  derivePaymentModel,
} from "@redotech/redo-model/coverage";
import { Onboarding } from "@redotech/redo-model/onboarding";
import {
  CoverageCreditIncentiveType,
  DynamicReturnTypes,
  Team,
} from "@redotech/redo-model/team";
import { RedoButton } from "@redotech/redo-web/arbiter-components/buttons/redo-button";
import {
  Button,
  ButtonBorder,
  ButtonSize,
  ButtonTheme,
  IconButton,
  IconButtonTheme,
} from "@redotech/redo-web/button";
import { Checkbox, CheckboxGroup } from "@redotech/redo-web/checkbox";
import { ChipDelimiter, FormChipInput } from "@redotech/redo-web/chip-input";
import { DateInput } from "@redotech/redo-web/date-picker";
import { Divider } from "@redotech/redo-web/divider";
import { Flex } from "@redotech/redo-web/flex";
import * as gridCss from "@redotech/redo-web/grid.module.css";
import PlusIcon from "@redotech/redo-web/icon-old/plus.svg";
import TrashIcon from "@redotech/redo-web/icon-old/trash.svg";
import { LabeledInput } from "@redotech/redo-web/labeled-input";
import {
  FormMultiSelectDropdown,
  FormSelectDropdown,
} from "@redotech/redo-web/select-dropdown";
import {
  PriceBracketControls,
  Pricing,
  pricingRuleSetDefault,
  pricingRuleSetForm,
  pricingRulesConvertTeamToValue,
} from "@redotech/redo-web/settings-elements/dynamic-pricing";
import {
  DynamicCreditsForm,
  DynamicCreditsTierForm,
  FinalSaleReturnsForm,
  validateDynamicCredits,
} from "@redotech/redo-web/settings-elements/final-sale";
import { InternationalPriceRoundingButton } from "@redotech/redo-web/settings-elements/international-price-rounding";
import { FormSwitch } from "@redotech/redo-web/switch";
import { Tabs } from "@redotech/redo-web/tab";
import { Text } from "@redotech/redo-web/text";
import { FormTextInput } from "@redotech/redo-web/text-input";
import { formatDate } from "@redotech/redo-web/time";
import { Tooltip } from "@redotech/redo-web/tooltip/tooltip";
import {
  InputProvider,
  ValidationError,
  groupInput,
  input,
  listInput,
  nonEmptyValidator,
  numberValidator,
} from "@redotech/ui/form";
import {
  arrayEqual,
  dateEqual,
  nullableEqual,
  objectEqual,
  stringEqual,
} from "@redotech/util/equal";
import classNames from "classnames";
import { produce } from "immer";
import { ReactNode, memo, useContext, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { RedoAdminClientContext } from "../../../../client/context";
import { TeamContext } from "../../../team";
import * as teamCss from "../../../team.module.css";
import { CollectionAutocomplete } from "../collections-autocomplete";

const FACTOR_FORMAT = new Intl.NumberFormat(undefined, {
  maximumFractionDigits: 2,
  minimumFractionDigits: 2,
});

const PERCENT_FORMAT = new Intl.NumberFormat(undefined, {
  style: "percent",
  maximumFractionDigits: 0,
});

export interface DateRange {
  start: Date | null;
  end: Date | null;
}
export enum ReturnsPricingEnum {
  FIXED = "FIXED",
  PER_ITEM = "PER_ITEM",
  ORDER_TOTAL = "ORDER_TOTAL",
}
export const internationalPricingForm = groupInput({
  id: input<symbol>(),
  countries: input<string[]>(),
  multiplier: input<string>({ validator: numberValidator({ min: 1 }) }),
});

export type InternationalPricingValue = InputProvider.Value<
  typeof internationalPricingForm
>;
export type InternationalPricingForm = InputProvider.Form<
  typeof internationalPricingForm
>;

export const internationalPricingDefault = (
  id: symbol,
): InternationalPricingValue => ({ id, countries: [], multiplier: "1.5" });

const internationalPricingListForm = listInput(
  () => internationalPricingForm,
  internationalPricingDefault,
  (i) => i.id,
);

export type InternationalPricingListForm = InputProvider.Form<
  typeof internationalPricingListForm
>;
export const returnsCoverageForm = groupInput(
  {
    coverage: groupInput({
      credit: input<boolean>(),
      exchange: input<boolean>(),
      refund: input<boolean>(),
    }),
    merchantPricePerOrder: input<string>(),
    rmrProcessingCoverageShare: input<string>(),
    rmrConsolidationCoverageShare: input<string>(),
    excludeCoverageIfAnyInvalidProduct: input<boolean>(),
    pricePerOrder: input<string>(),
    serviceCountries: input<string[]>({ equal: arrayEqual(stringEqual) }),
    merchantPaidEnabledDates: input<DateRange[]>({
      equal: arrayEqual(
        objectEqual({
          start: nullableEqual(dateEqual),
          end: nullableEqual(dateEqual),
        }),
      ),
      validator: (value) => {
        const errors: ValidationError[] = [];
        for (const range of value) {
          if (range.start == null) {
            errors.push("Required");
          } else if (range.start && range.end && range.start > range.end) {
            errors.push("Start must be before end");
          }
        }
        return errors;
      },
    }),
    returnPricingSelection: input<ReturnsPricingEnum>(),
    pricePerAdditonalItem: input<string>({ validator: nonEmptyValidator }),
    dynamicPriceCartValueRuleSet: listInput(
      () => pricingRuleSetForm,
      pricingRuleSetDefault,
      (_, index) => index,
    ),
    maxPrice: input<string>({ validator: nonEmptyValidator }),
    internationalPricing: internationalPricingListForm,
    coverageCreditIncentive: groupInput({
      enabled: input<boolean>(),
      type: input<CoverageCreditIncentiveType>(),
      fixedAmount: input<string>(),
      percentageSettings: groupInput({
        percentage: input<string>(),
        storeCreditAmountCap: input<string>(),
      }),
      minimumQualifyingPurchaseAmount: input<string>(),
      creditExpirationDays: input<string>(),
      sendEmail: input<boolean>(),
    }),
  },
  {
    validator: (value) => {
      const countrySet = new Set(value.serviceCountries);
      let haveAllCountries = value.serviceCountries.length === 0;

      const hasDuplicateCountry = value.internationalPricing.some((pricing) => {
        const empty = pricing.countries.length === 0;
        const hasDuplicateCountry = pricing.countries.some((country) => {
          const hasDuplicate = countrySet.has(country);
          countrySet.add(country);
          return hasDuplicate;
        });

        if (empty && haveAllCountries) {
          return true;
        } else if (empty) {
          haveAllCountries = true;
        }

        return hasDuplicateCountry;
      });

      if (hasDuplicateCountry) {
        return ["Invalid country setup between domestic and international"];
      }
      return [];
    },
  },
);

export const returnsCoverageTeamToFormValue = (
  team: Team,
): ReturnsCoverageValue => {
  return {
    coverage: {
      credit: team.settings.coverage.storeCredit,
      exchange: team.settings.coverage.exchange,
      refund: team.settings.coverage.refund,
    },
    excludeCoverageIfAnyInvalidProduct:
      team.settings.excludeCoverageIfAnyInvalidProduct ?? false,
    merchantPaidEnabledDates:
      team.settings?.merchantPaidEnabledDates?.map((range) => ({
        start: range.start ? new Date(Date.parse(range.start)) : null,
        end: range.end ? new Date(Date.parse(range.end)) : null,
      })) || [],
    merchantPricePerOrder: team.merchantPricePerOrder || "",
    rmrProcessingCoverageShare:
      team.settings.redoManagedReturns?.processingCoverageShare?.toString() ??
      "",
    rmrConsolidationCoverageShare:
      team.settings.redoManagedReturns?.consolidationCoverageShare?.toString() ??
      "",
    pricePerOrder: team.pricePerOrder,
    serviceCountries: team.settings.countries,
    returnPricingSelection: team.settings.returns?.dynamicPricing
      ? team.settings.returns.dynamicPricing.type ===
        DynamicReturnTypes.ITEM_COUNT
        ? ReturnsPricingEnum.PER_ITEM
        : ReturnsPricingEnum.ORDER_TOTAL
      : ReturnsPricingEnum.FIXED,
    pricePerAdditonalItem:
      team.settings.returns?.dynamicPricing?.type ===
      DynamicReturnTypes.ITEM_COUNT
        ? String(team.settings.returns?.dynamicPricing?.pricePerAdditonalItem)
        : "2",
    maxPrice: team.settings.returns?.dynamicPricing?.maxPrice
      ? String(team.settings.returns.dynamicPricing.maxPrice)
      : "5",
    internationalPricing:
      team.settings.returns?.internationalPricing?.map((pricing, i) => {
        return {
          countries: pricing.countries,
          multiplier: pricing.multiplier.toFixed(2),
          id: Symbol(i),
        };
      }) || [],
    coverageCreditIncentive: {
      enabled:
        team.settings?.shopifyExtension?.coverageCreditIncentive?.enabled ??
        false,
      type:
        team.settings?.shopifyExtension?.coverageCreditIncentive?.type ??
        CoverageCreditIncentiveType.FIXED,
      fixedAmount: String(
        team.settings?.shopifyExtension?.coverageCreditIncentive?.fixedAmount ??
          5,
      ),
      percentageSettings: {
        percentage: String(
          team.settings?.shopifyExtension?.coverageCreditIncentive
            ?.percentageSettings?.percentage ?? 5,
        ),
        storeCreditAmountCap: team.settings?.shopifyExtension
          ?.coverageCreditIncentive?.percentageSettings?.storeCreditAmountCap
          ? String(
              team.settings.shopifyExtension.coverageCreditIncentive
                .percentageSettings.storeCreditAmountCap,
            )
          : "",
      },
      minimumQualifyingPurchaseAmount: String(
        team.settings?.shopifyExtension?.coverageCreditIncentive
          ?.minimumQualifyingPurchaseAmount ?? 0,
      ),
      creditExpirationDays: String(
        team.settings?.shopifyExtension?.coverageCreditIncentive
          ?.creditExpirationDays ?? "",
      ),
      sendEmail:
        team.settings?.shopifyExtension?.coverageCreditIncentive?.sendEmail ??
        true,
    },
    dynamicPriceCartValueRuleSet: pricingRulesConvertTeamToValue(
      team.settings.returns?.dynamicPricing?.type ===
        DynamicReturnTypes.CART_VALUE
        ? [team.settings.returns?.dynamicPricing.pricingRuleSet]
        : undefined,
    ),
  };
};

export type ReturnsCoverageForm = InputProvider.Form<
  typeof returnsCoverageForm
>;

export type ReturnsCoverageValue = InputProvider.Value<
  typeof returnsCoverageForm
>;

export const returnsCoverageDefault: ReturnsCoverageValue = {
  coverage: { credit: false, exchange: false, refund: false },
  excludeCoverageIfAnyInvalidProduct: false,
  merchantPricePerOrder: "",
  rmrProcessingCoverageShare: "",
  rmrConsolidationCoverageShare: "",
  merchantPaidEnabledDates: [],
  pricePerOrder: "",
  serviceCountries: [],
  returnPricingSelection: ReturnsPricingEnum.FIXED,
  pricePerAdditonalItem: "",
  maxPrice: "",
  internationalPricing: [],
  dynamicPriceCartValueRuleSet: [
    {
      type: "percentage",
      countries: [],
      priceBrackets: [{ value: "2", pricePoint: "0" }],
    },
  ],
  coverageCreditIncentive: {
    enabled: false,
    type: CoverageCreditIncentiveType.FIXED,
    fixedAmount: "0",
    percentageSettings: { percentage: "0", storeCreditAmountCap: "" },
    minimumQualifyingPurchaseAmount: "0",
    creditExpirationDays: "",
    sendEmail: true,
  },
};

const InternationalReturnsPricing = memo(function InternationalReturnsPricing({
  pricing,
  countryErrors,
}: {
  pricing: InternationalPricingListForm;
  countryErrors: string[];
}) {
  function deleteRow(id: symbol) {
    return () => {
      pricing.setValue(pricing.value.filter((price) => price.id !== id));
    };
  }

  return (
    <>
      {pricing.value.length > 0 && (
        <div className={classNames(gridCss.grid, gridCss.span12)}>
          <div className={classNames(gridCss.span1)} />
          <div className={classNames(gridCss.span9)}>Countries</div>
          <div className={classNames(gridCss.span2)}>Multiplier</div>
        </div>
      )}
      <IterableMap items={pricing.inputs} keyFn={(_, i) => i}>
        {(input: InternationalPricingForm) => {
          const {
            countries: internationalCountries,
            multiplier,
            id,
          } = input.inputs;
          return (
            <div className={classNames(gridCss.grid, gridCss.span12)}>
              <div className={classNames(gridCss.span1)}>
                <IconButton onClick={deleteRow(id.value)}>
                  <div className={classNames()}>
                    <TrashIcon />
                  </div>
                </IconButton>
              </div>
              <div className={classNames(gridCss.span8)}>
                <FormMultiSelectDropdown
                  display={(value) => {
                    if (value.length === 0) {
                      return "Everywhere else";
                    }
                    if (value.length === countries.length) {
                      return "Everywhere else";
                    }
                    const names = value.map(
                      (code) =>
                        countries.find((country) => country.code === code)
                          ?.name,
                    );
                    return names.join(", ");
                  }}
                  input={internationalCountries}
                  label=""
                  options={countries.map((country: any) => country.code)}
                >
                  {(option) =>
                    countries.find((country) => country.code === option)?.name
                  }
                </FormMultiSelectDropdown>
              </div>
              <div className={classNames(gridCss.span3)}>
                <FormTextInput
                  description=""
                  input={multiplier}
                  label=""
                  prefix="x"
                  type="number"
                />
              </div>
            </div>
          );
        }}
      </IterableMap>

      <div className={classNames(gridCss.span12, teamCss.centered)}>
        <Button
          border={ButtonBorder.LIGHT}
          onClick={() =>
            pricing.setValue([
              ...pricing.value,
              { id: Symbol(), countries: [], multiplier: "1.5" },
            ])
          }
          theme={ButtonTheme.OUTLINED}
        >
          Add International Countries
        </Button>
      </div>

      <div className={classNames(gridCss.span12, teamCss.centered)}>
        <span className={classNames(teamCss.errorText)}>
          {countryErrors?.[0]}
        </span>
      </div>
    </>
  );
});

enum LocationSet {
  DOMESTIC = "Domestic",
  INTERNATIONAL = "International",
}

const LocationSetLables = {
  [LocationSet.DOMESTIC]: <>Domestic</>,
  [LocationSet.INTERNATIONAL]: <>International</>,
};

const DynamicReturnsPricingCartValue = memo(
  function DynamicReturnsPricingCartValue({
    input,
  }: {
    input: ReturnsCoverageForm;
  }) {
    const {
      merchantPricePerOrder,
      dynamicPriceCartValueRuleSet,
      maxPrice,
      pricePerOrder,
    } = input.inputs;

    const revShareRatio = useMemo(() => {
      return (+merchantPricePerOrder.value / +pricePerOrder.value) * 100;
    }, [merchantPricePerOrder.value, pricePerOrder.value]);

    const team = useContext(TeamContext);
    return (
      <div className={classNames(gridCss.span12, gridCss.grid)} dir="column">
        <div className={gridCss.span6L}>
          <FormTextInput
            description="base price of redo"
            input={pricePerOrder}
            label="Min Price"
            prefix={getCurrencySymbol(team?._shopify.currency || Currency.USD)}
            suffix={
              team?._shopify.currency === "USD" ? "" : team?._shopify.currency
            }
          />
        </div>
        <div className={gridCss.span6L}>
          <FormTextInput
            description={`For dynamic pricing, use the "Minimum" field and this field to set the ratio of coverage at which the Redo will bill the merchant.
For example if backend price = $.30 and base coverage = $1.00, Redo will bill the merchant at 30% of what the customer purchases for coverage.
For rev share pricing, set this field below the minimum.
For merchant-subsidized pricing, set this field above the minimum.
For CommentSold accounts, this field functions as a flat "Backend price." We will bill the merchant at whatever amount is input for this field.`}
            input={merchantPricePerOrder}
            label={`Rev share (ratio: ${revShareRatio.toFixed(2)}%)`}
            placeholder="0.00 (no rev share)"
            prefix={getCurrencySymbol(team?._shopify.currency || Currency.USD)}
            suffix={
              team?._shopify.currency === "USD" ? "" : team?._shopify.currency
            }
          />
        </div>
        <div className={classNames(gridCss.span6L)}>
          <FormTextInput
            description=""
            input={maxPrice}
            label="Max price"
            min={+pricePerOrder.value}
            prefix={getCurrencySymbol(team?._shopify.currency || Currency.USD)}
            step={0.01}
            suffix={
              team?._shopify.currency === "USD" ? "" : team?._shopify.currency
            }
            type="number"
          />
        </div>

        <div className={classNames(gridCss.span12)}>
          <PriceBracketControls
            adminApp
            canEditSettings
            index={0}
            minMaxErrors={input.errors}
            // @ts-expect-error TODO: noUncheckedIndexedAccess FIXME
            pricingRuleSet={dynamicPriceCartValueRuleSet.inputs[0]}
          />
        </div>
      </div>
    );
  },
);

export const ReturnsCoverageCard = memo(function ReturnsCoverageCard({
  input,
  onboarding,
}: {
  input: ReturnsCoverageForm;
  onboarding: Onboarding | undefined;
}) {
  const {
    coverage,
    excludeCoverageIfAnyInvalidProduct,
    merchantPaidEnabledDates,
    merchantPricePerOrder,
    pricePerOrder,
    serviceCountries,
    returnPricingSelection,
    pricePerAdditonalItem,
    maxPrice,
    internationalPricing,
  } = input.inputs;
  const { credit, exchange, refund } = coverage.inputs;
  const [property, setProperty] = useState(LocationSet.DOMESTIC);

  const team = useContext(TeamContext);
  const client = useContext(RedoAdminClientContext);
  const currencyConverter = useContext(CurrencyConverterContext);

  const exchangeRatesLoad = useLoad(async () => {
    if (
      !team?._shopify.currency ||
      team._shopify.currency === Currency.USD ||
      !client
    ) {
      return null;
    }
    return await currencyConverter();
  }, [team]);

  const derivedPaymentModel = derivePaymentModel(
    input.value.merchantPaidEnabledDates.map((d) => ({
      start: d.start?.toISOString() ?? null,
      end: d.end?.toISOString() ?? null,
    })),
  );

  const ratio = calcPricePerOrder(
    {
      exchange: input.value.coverage.exchange,
      refund: input.value.coverage.refund,
      storeCredit: input.value.coverage.credit,
    },
    derivedPaymentModel,
    { amount: 1, currency: Currency.USD },
  ).amount;

  const calcBase =
    onboarding?.orders &&
    ((2 * onboarding.orders.realRefundPercentage) / 100) *
      +onboarding.labelPrice;

  const revShareRatio = useMemo(() => {
    return (+merchantPricePerOrder.value / +pricePerOrder.value) * 100;
  }, [merchantPricePerOrder.value, pricePerOrder.value]);

  const priceDiff = useMemo(() => {
    const shopperPrice = parseFloat(pricePerOrder.value) || 0;
    const merchantPrice =
      parseFloat(merchantPricePerOrder.value) || shopperPrice;

    return merchantPrice - shopperPrice;
  }, [merchantPricePerOrder.value, pricePerOrder.value]);

  const hasOpenEndedMerchantPaidDates = merchantPaidEnabledDates.value.some(
    (range) => !range.end,
  );
  const coveragePriceIsNonZero =
    parseFloat(pricePerOrder.value) !== 0 && pricePerOrder.value !== "";
  const merchantBilledPriceIsMissing =
    !merchantPricePerOrder.value ||
    parseFloat(merchantPricePerOrder.value) === 0;
  const showMerchantPaidWarning =
    hasOpenEndedMerchantPaidDates &&
    (coveragePriceIsNonZero || merchantBilledPriceIsMissing);

  function MerchantPaidEnabledDates() {
    const handleChange = useHandler(
      (date: Date | null, index: number, placement: string) => {
        const { merchantPaidEnabledDates } = input.inputs;
        if (placement === "start") {
          merchantPaidEnabledDates.setValue(
            produce(merchantPaidEnabledDates.value, (dates: any) => {
              dates[index].start = date;
            }),
          );
        } else {
          merchantPaidEnabledDates.setValue(
            produce(merchantPaidEnabledDates.value, (dates: any) => {
              dates[index].end = date;
            }),
          );
        }
      },
    );

    return (
      <>
        {merchantPaidEnabledDates.value.map(
          (range: { start: Date | null; end: Date | null }, index: number) => (
            <div className={teamCss.merchantPaidDateRange} key={index}>
              <div className={teamCss.date}>
                <LabeledInput label="Start">
                  <DateInput
                    value={range.start}
                    valueChange={(val) => handleChange(val, index, "start")}
                  />
                </LabeledInput>
              </div>
              <div className={teamCss.date}>
                <LabeledInput label="End (leave blank for active)">
                  <DateInput
                    value={range.end}
                    valueChange={(val) => handleChange(val, index, "end")}
                  />
                </LabeledInput>
              </div>
            </div>
          ),
        )}
      </>
    );
  }

  return (
    <section className={gridCss.grid}>
      <div className={gridCss.span6}>
        <LabeledInput label="Covered return methods">
          <CheckboxGroup>
            <Checkbox onChange={credit.setValue} value={credit.value}>
              Store credit
            </Checkbox>
            <Checkbox onChange={exchange.setValue} value={exchange.value}>
              Exchange
            </Checkbox>
            <Checkbox onChange={refund.setValue} value={refund.value}>
              Refund
            </Checkbox>
          </CheckboxGroup>
        </LabeledInput>
      </div>
      <div className={gridCss.span6}>
        <FormSwitch
          input={excludeCoverageIfAnyInvalidProduct}
          label="Exclude coverage on invalid products"
        >
          Do not offer coverage if any product in the cart is invalid
        </FormSwitch>
      </div>
      <div className={classNames(gridCss.span12)}>
        <LabeledInput
          description="All dates are start of day UTC. The end date is excluded from merchant paid coverage."
          label="Merchant paid coverage"
        >
          {MerchantPaidEnabledDates()}
          <div
            className={teamCss.add}
            onClick={() =>
              merchantPaidEnabledDates.setValue([
                ...merchantPaidEnabledDates.value,
                { start: null, end: null },
              ])
            }
          >
            Add date range
            <PlusIcon />
          </div>
        </LabeledInput>
      </div>

      <div className={classNames(gridCss.span12)}>
        <Tabs<LocationSet>
          keyFn={(option) => option}
          options={[LocationSet.DOMESTIC, LocationSet.INTERNATIONAL]}
          tab={(option) => LocationSetLables[option]}
          value={property}
          valueChange={setProperty}
        >
          {property === LocationSet.DOMESTIC ? (
            <div className={classNames(gridCss.grid, gridCss.span12)}>
              <div className={gridCss.span12}>
                <FormSelectDropdown
                  input={returnPricingSelection}
                  label="Pricing Strategy"
                  options={Object.values(ReturnsPricingEnum)}
                >
                  {(option) => {
                    switch (option) {
                      case ReturnsPricingEnum.FIXED:
                        return "Single Fixed Price";
                      case ReturnsPricingEnum.ORDER_TOTAL:
                        return "Dynamic: cart total";
                      case ReturnsPricingEnum.PER_ITEM:
                        return "Dynamic: number of items";
                    }
                  }}
                </FormSelectDropdown>
              </div>

              {returnPricingSelection.value === ReturnsPricingEnum.PER_ITEM && (
                <div className={classNames(gridCss.span12, gridCss.grid)}>
                  <div className={gridCss.span6}>
                    <FormTextInput
                      description="Price of redo with one item in cart"
                      input={pricePerOrder}
                      label="Base price"
                      prefix={getCurrencySymbol(
                        team?._shopify.currency || Currency.USD,
                      )}
                      suffix={
                        team?._shopify.currency === "USD"
                          ? ""
                          : team?._shopify.currency
                      }
                      textAlign="left"
                    />
                  </div>
                  <div className={gridCss.span6}>
                    <FormTextInput
                      description={`For dynamic pricing, use the "Minimum" field and this field to set the ratio of coverage at which the Redo will bill the merchant.
For example if backend price = $.30 and base coverage = $1.00, Redo will bill the merchant at 30% of what the customer purchases for coverage.
For rev share pricing, set this field below the minimum.
For merchant-subsidized pricing, set this field above the minimum.
For CommentSold accounts, this field functions as a flat "Backend price." We will bill the merchant at whatever amount is input for this field.`}
                      input={merchantPricePerOrder}
                      label={`Rev share (ratio: ${revShareRatio.toFixed(2)}%)`}
                      placeholder="0.00 (no rev share)"
                      prefix={getCurrencySymbol(
                        team?._shopify.currency || Currency.USD,
                      )}
                      suffix={
                        team?._shopify.currency === "USD"
                          ? ""
                          : team?._shopify.currency
                      }
                      textAlign="left"
                    />
                  </div>
                  <div className={gridCss.span6}>
                    <FormTextInput
                      description=""
                      input={pricePerAdditonalItem}
                      label="Price per additional item"
                      min={0.01}
                      prefix={getCurrencySymbol(
                        team?._shopify.currency || Currency.USD,
                      )}
                      step={0.01}
                      suffix={
                        team?._shopify.currency === "USD"
                          ? ""
                          : team?._shopify.currency
                      }
                      textAlign="left"
                      type="number"
                    />
                  </div>
                  <div className={gridCss.span6}>
                    <FormTextInput
                      description=""
                      input={maxPrice}
                      label="Max price"
                      min={+pricePerOrder.value}
                      prefix={getCurrencySymbol(
                        team?._shopify.currency || Currency.USD,
                      )}
                      step={0.01}
                      suffix={
                        team?._shopify.currency === "USD"
                          ? ""
                          : team?._shopify.currency
                      }
                      textAlign="left"
                      type="number"
                    />
                  </div>
                </div>
              )}
              {returnPricingSelection.value === ReturnsPricingEnum.FIXED && (
                <div className={classNames(gridCss.span12, gridCss.grid)}>
                  <div className={gridCss.span6}>
                    <FormTextInput
                      description="Amount the shopper pays to the merchant on the storefront."
                      errorOverride={
                        showMerchantPaidWarning && coveragePriceIsNonZero
                      }
                      fullwidth
                      input={pricePerOrder}
                      label="Coverage Price"
                      prefix={getCurrencySymbol(
                        team?._shopify.currency || Currency.USD,
                      )}
                      step={0.01}
                      suffix={
                        team?._shopify.currency === "USD"
                          ? ""
                          : team?._shopify.currency
                      }
                      textAlign="left"
                      type="number"
                    />
                    {showMerchantPaidWarning && coveragePriceIsNonZero && (
                      <Text
                        fontSize="xs"
                        fontWeight="semibold"
                        textColor="error"
                      >
                        Merchant paid coverage requires Coverage Price to be 0.
                        Payout orders will not be generated otherwise.
                      </Text>
                    )}
                  </div>
                  <div className={gridCss.span6}>
                    <FormTextInput
                      description={
                        "The amount the merchant pays to Redo for coverage." +
                        (priceDiff > 0
                          ? ` (Merchant pays $${FACTOR_FORMAT.format(priceDiff)} out of pocket)`
                          : priceDiff < 0
                            ? ` (Merchant keeps $${FACTOR_FORMAT.format(Math.abs(priceDiff))})`
                            : "")
                      }
                      errorOverride={
                        showMerchantPaidWarning && merchantBilledPriceIsMissing
                      }
                      fullwidth
                      input={merchantPricePerOrder}
                      label="Merchant Billed Price"
                      onKeyDown={(e) => {
                        if (
                          e.key === "Backspace" &&
                          merchantPricePerOrder.value.length === 1
                        ) {
                          merchantPricePerOrder.setValue("");
                        }
                      }}
                      placeholder={pricePerOrder.value}
                      prefix={getCurrencySymbol(
                        team?._shopify.currency || Currency.USD,
                      )}
                      step={0.01}
                      suffix={
                        team?._shopify.currency === "USD"
                          ? ""
                          : team?._shopify.currency
                      }
                      textAlign="left"
                      type="number"
                    />
                    {showMerchantPaidWarning &&
                      merchantBilledPriceIsMissing && (
                        <Text
                          fontSize="xs"
                          fontWeight="semibold"
                          textColor="error"
                        >
                          Merchant Billed Price must be set for merchant paid
                          coverage to generate payout orders.
                        </Text>
                      )}
                  </div>
                </div>
              )}
              {returnPricingSelection.value ===
                ReturnsPricingEnum.ORDER_TOTAL && (
                <DynamicReturnsPricingCartValue input={input} />
              )}
              <RmrCoverageAttributionSummary
                consolidationShare={Number(
                  input.inputs.rmrConsolidationCoverageShare.value,
                )}
                currency={team?._shopify.currency || Currency.USD}
                processingShare={Number(
                  input.inputs.rmrProcessingCoverageShare.value,
                )}
              />
              <div className={classNames(gridCss.span12)}>
                <FormMultiSelectDropdown
                  display={(value) => {
                    if (value.length === 0) {
                      return "All";
                    }
                    if (value.length === countries.length) {
                      return "All";
                    }
                    const names = value.map(
                      (code) =>
                        countries.find((country) => country.code === code)
                          ?.name,
                    );
                    return names.join(", ");
                  }}
                  input={serviceCountries}
                  label="Domestic countries"
                  options={countries.map((country: any) => country.code)}
                >
                  {(option) =>
                    countries.find((country) => country.code === option)?.name
                  }
                </FormMultiSelectDropdown>
              </div>
              <div className={gridCss.span12}>
                <InternationalPriceRoundingButton />
              </div>

              {input.allErrors?.[0] && (
                <div className={classNames(gridCss.span12, teamCss.centered)}>
                  <span className={classNames(teamCss.errorText)}>
                    {input.allErrors[0]}
                  </span>
                </div>
              )}
            </div>
          ) : (
            <div className={classNames(gridCss.span12, teamCss.flexOne)}>
              <InternationalReturnsPricing
                countryErrors={input.allErrors}
                pricing={internationalPricing}
              />
              <div style={{ marginTop: "16px" }}>
                <InternationalPriceRoundingButton />
              </div>
            </div>
          )}
        </Tabs>
      </div>

      {/* Coverage Credit Incentive Section */}
      <div className={gridCss.span12}>
        <Divider />
      </div>
      <div className={gridCss.span12}>
        <h3>Coverage Credit Incentive</h3>
      </div>
      <div className={classNames(gridCss.span12, gridCss.span6L)}>
        <FormSwitch
          input={input.inputs.coverageCreditIncentive.inputs.enabled}
          label="Enable coverage credit incentive"
        >
          Give store credit to customers who purchase coverage
        </FormSwitch>
      </div>

      {input.inputs.coverageCreditIncentive.inputs.enabled.value && (
        <>
          <div className={classNames(gridCss.span12, gridCss.span6L)}>
            <FormSelectDropdown
              input={input.inputs.coverageCreditIncentive.inputs.type}
              label="Incentive type"
              options={[
                CoverageCreditIncentiveType.FIXED,
                CoverageCreditIncentiveType.PERCENTAGE,
              ]}
            >
              {(option) => {
                switch (option) {
                  case CoverageCreditIncentiveType.FIXED:
                    return "Fixed amount";
                  case CoverageCreditIncentiveType.PERCENTAGE:
                    return "Percentage of order";
                  default:
                    return option;
                }
              }}
            </FormSelectDropdown>
          </div>

          <div className={classNames(gridCss.span12, gridCss.span6L)}>
            <FormTextInput
              description="Minimum order amount (excluding coverage product) to qualify for credit"
              input={
                input.inputs.coverageCreditIncentive.inputs
                  .minimumQualifyingPurchaseAmount
              }
              label="Minimum qualifying purchase amount"
              min={0}
              prefix={getCurrencySymbol(
                team?._shopify.currency || Currency.USD,
              )}
              step="0.01"
              type="number"
            />
          </div>

          {input.inputs.coverageCreditIncentive.inputs.type.value ===
            CoverageCreditIncentiveType.FIXED && (
            <div className={classNames(gridCss.span12, gridCss.span6L)}>
              <FormTextInput
                description="Fixed store credit amount to give"
                input={input.inputs.coverageCreditIncentive.inputs.fixedAmount}
                label="Credit amount"
                min={0}
                prefix={getCurrencySymbol(
                  team?._shopify.currency || Currency.USD,
                )}
                step="0.01"
                type="number"
              />
            </div>
          )}

          {input.inputs.coverageCreditIncentive.inputs.type.value ===
            CoverageCreditIncentiveType.PERCENTAGE && (
            <>
              <div className={classNames(gridCss.span12, gridCss.span6L)}>
                <FormTextInput
                  description=""
                  errorOverride={
                    parseFloat(
                      input.inputs.coverageCreditIncentive.inputs
                        .percentageSettings.inputs.percentage.value,
                    ) < 0 ||
                    parseFloat(
                      input.inputs.coverageCreditIncentive.inputs
                        .percentageSettings.inputs.percentage.value,
                    ) > 100
                  }
                  input={
                    input.inputs.coverageCreditIncentive.inputs
                      .percentageSettings.inputs.percentage
                  }
                  label="Percentage of order"
                  max={100}
                  min={0}
                  step="0.01"
                  suffix="%"
                  type="number"
                />
              </div>

              <div className={classNames(gridCss.span12, gridCss.span6L)}>
                <FormTextInput
                  description="Maximum store credit amount (leave empty for no limit)"
                  input={
                    input.inputs.coverageCreditIncentive.inputs
                      .percentageSettings.inputs.storeCreditAmountCap
                  }
                  label="Maximum credit amount"
                  min={0}
                  prefix={getCurrencySymbol(
                    team?._shopify.currency || Currency.USD,
                  )}
                  step="0.01"
                />
              </div>
            </>
          )}

          <div className={classNames(gridCss.span12, gridCss.span6L)}>
            <FormTextInput
              description="Number of days until the store credit expires (leave empty for no expiration)"
              input={
                input.inputs.coverageCreditIncentive.inputs.creditExpirationDays
              }
              label="Credit expiration days"
              min={0}
            />
          </div>

          <div className={classNames(gridCss.span12)}>
            <FormSwitch
              input={input.inputs.coverageCreditIncentive.inputs.sendEmail}
              label="Send email notification"
            >
              Send an email notification to customers when they receive store
              credit
            </FormSwitch>
          </div>
        </>
      )}

      <div className={gridCss.span12}>
        <Divider />
      </div>
      <div className={gridCss.span12}>
        <strong>
          {onboarding?.finished
            ? "Onboarding order statistics"
            : "Order statistics not yet calculated"}
        </strong>
      </div>
      <div className={gridCss.span12}>
        <dl className={teamCss.table}>
          <dt className={teamCss.tableHeader}>Start</dt>
          <dd className={teamCss.tableCell}>
            {onboarding?.orders &&
              formatDate(Temporal.Instant.from(onboarding.orders.fromDate))}
          </dd>
          <dt className={teamCss.tableHeader}>End</dt>
          <dd className={teamCss.tableCell}>
            {onboarding?.orders &&
              formatDate(Temporal.Instant.from(onboarding.orders.toDate))}
          </dd>
          <dt className={teamCss.tableHeader}>Orders</dt>
          <dd className={teamCss.tableCell}>
            {onboarding?.orders &&
              new Intl.NumberFormat().format(onboarding.orders.count)}
          </dd>
          <dt className={teamCss.tableHeader}>Refunded orders</dt>
          <dd className={teamCss.tableCell}>
            {onboarding?.orders &&
              new Intl.NumberFormat().format(onboarding.orders.countRefunded)}
          </dd>
          <dt className={teamCss.tableHeader}>Refund rate</dt>
          <dd className={teamCss.tableCell}>
            {onboarding?.orders &&
              PERCENT_FORMAT.format(onboarding.orders.refundPercentage / 100)}
          </dd>
          <dt className={teamCss.tableHeader}>Estimated return rate</dt>
          <dd className={teamCss.tableCell}>
            {onboarding?.orders &&
              PERCENT_FORMAT.format(
                onboarding.orders.realRefundPercentage / 100,
              )}
          </dd>
          <dt className={teamCss.tableHeader}>Price per label</dt>
          <dd className={teamCss.tableCell}>
            {onboarding &&
              moneyFormat({
                amount: +onboarding.labelPrice,
                currency: Currency.USD,
              })}
          </dd>
          <dt className={teamCss.tableHeader}>Calculated base</dt>
          <dd className={teamCss.tableCell}>
            {calcBase !== undefined &&
              moneyFormat({ amount: calcBase, currency: Currency.USD })}
          </dd>
          <dt className={teamCss.tableHeader}>Calculated price</dt>
          <dd className={teamCss.tableCell}>
            {calcBase !== undefined &&
              moneyFormat({ amount: ratio * calcBase, currency: Currency.USD })}
          </dd>
          {exchangeRatesLoad.value && (
            <>
              <dt className={teamCss.tableHeader}>
                Calculated price in {team?._shopify.currency}{" "}
              </dt>
              <dd className={teamCss.tableCell}>
                {calcBase !== undefined &&
                  moneyFormat(
                    exchangeRatesLoad.value.convert(
                      { amount: ratio * calcBase, currency: Currency.USD },
                      team!._shopify.currency,
                    ),
                  )}
              </dd>
            </>
          )}
        </dl>
      </div>
    </section>
  );
});

const RmrCoverageAttributionSummary = memo(
  function RmrCoverageAttributionSummary({
    processingShare,
    consolidationShare,
    currency,
  }: {
    processingShare: number;
    consolidationShare: number;
    currency: Currency;
  }) {
    const total = processingShare + consolidationShare;
    if (total <= 0) {
      return null;
    }

    const symbol = getCurrencySymbol(currency);
    const parts: string[] = [];
    if (processingShare > 0) {
      parts.push(`${symbol}${processingShare.toFixed(2)} processing`);
    }
    if (consolidationShare > 0) {
      parts.push(`${symbol}${consolidationShare.toFixed(2)} consolidation`);
    }

    return (
      <div className={classNames(gridCss.span12)}>
        <Flex
          align="center"
          gap="md"
          justify="space-between"
          style={{
            padding: "10px 14px",
            borderRadius: 8,
            border: "1px solid var(--redo-colors-border-border-secondary)",
            background: "var(--redo-colors-background-bg-secondary)",
          }}
        >
          <Flex dir="column" gap="xxs">
            <Text fontSize="xs" fontWeight="medium" textColor="secondary">
              {`${symbol}${total.toFixed(2)} of coverage attributed to RMR`}
            </Text>
            <Text fontSize="xxs" textColor="tertiary">
              {parts.join(" · ")}
            </Text>
          </Flex>
          <Link to="../../recommerce">
            <Text fontSize="xs" fontWeight="medium" textColor="brand-primary">
              Edit
            </Text>
          </Link>
        </Flex>
      </div>
    );
  },
);

/**
 * Wraps a Dynamic-FSR form input in a Tooltip showing the field's validation
 * error message (when one exists). The wrapping `<div>` keeps the cell's grid
 * span and provides an anchor for the Tooltip; the input itself still gets a
 * red border via its own `errorOverride` prop, which acts as the always-on
 * indicator that something is wrong.
 *
 * The Tooltip is always rendered (with hover/focus/touch listeners disabled
 * when there's no error) so that React keeps the same element tree across
 * error transitions — otherwise toggling between a `<div>` and a `<Tooltip>`
 * root remounts the inner FormTextInput on every keystroke and steals focus.
 *
 * No vertical space is reserved for error text — that keeps row-to-row
 * spacing tight and consistent regardless of validity.
 */
const ErrorTooltip = memo(function ErrorTooltip({
  error,
  className,
  children,
}: {
  error: string | null;
  className?: string;
  children: ReactNode;
}) {
  return (
    <Tooltip
      arrow
      disableFocusListener={!error}
      disableHoverListener={!error}
      disableTouchListener={!error}
      placement="top"
      title={error ? <Text>{error}</Text> : " "}
    >
      <div className={className}>{children}</div>
    </Tooltip>
  );
});

const DynamicCreditsSection = memo(function DynamicCreditsSection({
  input,
}: {
  input: DynamicCreditsForm;
}) {
  const { enabled, expectedReturnRate, lookbackWindowDays, tiers } =
    input.inputs;

  const removeTier = useHandler((indexToRemove: number) => {
    tiers.setValue(tiers.value.filter((_, index) => index !== indexToRemove));
  });

  const addTier = useHandler(() => {
    tiers.setValue([...tiers.value, { thresholdRate: "", rate: "" }]);
  });

  const validation = useMemo(
    () => validateDynamicCredits(input.value),
    [input.value],
  );

  // Threshold inputs only make sense when there's a valid priced return
  // rate to anchor them to — the form-to-team converter divides by it on
  // save. Disable the threshold cells until the merchant has set one.
  const pricedReturnRateValid = useMemo(() => {
    if (expectedReturnRate.value.trim() === "") {
      return false;
    }
    const n = Number(expectedReturnRate.value);
    return Number.isFinite(n) && n > 0 && n <= 100;
  }, [expectedReturnRate.value]);

  // Paste handler — fills the grid from a tab/newline-delimited block
  // (e.g. copied from a spreadsheet or the team's pricing-generator app).
  // The first row of the paste lands in the bracket the user pasted into
  // and subsequent rows fill the next brackets, extending the list when
  // the paste is taller than what's currently rendered. `%`, `$`, and
  // commas are stripped so pastes from common spreadsheet formats are
  // forgiving.
  const handleTierPaste = useHandler(
    (
      event: React.ClipboardEvent<HTMLInputElement | HTMLTextAreaElement>,
      startRow: number,
      startColumn: "thresholdRate" | "rate",
    ) => {
      const text = event.clipboardData.getData("text");
      if (!text.includes("\t") && !text.includes("\n")) {
        // Single-cell paste — let the input handle it normally.
        return;
      }
      event.preventDefault();
      const sanitize = (cell: string) =>
        cell.trim().replace(/[%$,]/g, "").trim();
      const grid = text
        .replace(/\r\n/g, "\n")
        .replace(/\r/g, "\n")
        .split("\n")
        .filter((row) => row.length > 0)
        .map((row) => row.split("\t").map(sanitize));
      if (grid.length === 0) {
        return;
      }
      const next = tiers.value.map((tier) => ({ ...tier }));
      for (const [rowOffset, row] of grid.entries()) {
        const targetIndex = startRow + rowOffset;
        while (next.length <= targetIndex) {
          next.push({ thresholdRate: "", rate: "" });
        }
        const cell = next[targetIndex]!;
        if (startColumn === "thresholdRate") {
          if (row[0] !== undefined) {
            cell.thresholdRate = row[0];
          }
          if (row[1] !== undefined) {
            cell.rate = row[1];
          }
        } else if (row[0] !== undefined) {
          // Pasting into the rate column — only consume the first column
          // of each row. Any extra columns have no obvious target, so
          // ignore them rather than silently overwriting an unrelated
          // bracket.
          cell.rate = row[0];
        }
      }
      tiers.setValue(next);
    },
  );

  return (
    <>
      <div className={classNames(gridCss.span12)}>
        <Divider />
      </div>
      <div className={classNames(gridCss.span12)}>
        <Flex align="center" flexDirection="row" gap="sm">
          <strong>Dynamic FSR adjustment</strong>
          <Tooltip
            arrow
            placement="bottom"
            title={
              <Text>
                Final sale returns (FSR) credits are issued up front when a
                customer keeps a final-sale item, at the merchant&apos;s flat
                Reimbursement Percentage above. Dynamic FSR adds an invoice-time
                correction: if the merchant&apos;s actual return rate runs
                higher than what they contracted for, we charge back some of the
                credit we issued.
                <br />
                <br />
                Per-return credit issuance is not changed — only the
                end-of-period charge-back is affected.
              </Text>
            }
          >
            <Flex>
              <HelpIcon style={{ width: "16px", height: "16px" }} />
            </Flex>
          </Tooltip>
        </Flex>
      </div>
      <div className={gridCss.span12}>
        <FormSwitch input={enabled} label="Dynamic FSR enabled">
          When on, at the end of each billing period we charge back any FSR
          credit that was over-issued because the merchant&apos;s actual return
          rate exceeded their expected rate.
        </FormSwitch>
      </div>
      {enabled.value && (
        <>
          <ErrorTooltip
            className={classNames(gridCss.span6)}
            error={validation.fieldErrors.expectedReturnRate}
          >
            <FormTextInput
              allowInvalidNumbers
              description="The return rate baked into the merchant's pricing (e.g. 1% of covered order value). Tier caps in the table below are multiples of this rate."
              errorOverride={!!validation.fieldErrors.expectedReturnRate}
              input={expectedReturnRate}
              label="Priced return rate"
              max={100}
              min={0.01}
              step={0.01}
              suffix="%"
              type="number"
            />
          </ErrorTooltip>
          <ErrorTooltip
            className={classNames(gridCss.span6)}
            error={validation.fieldErrors.lookbackWindowDays}
          >
            <FormTextInput
              allowInvalidNumbers
              description="How many days of completed orders to include when measuring the merchant's actual return rate at the end of each billing period (≥ 21 days). Leave blank to disable the adjustment."
              errorOverride={!!validation.fieldErrors.lookbackWindowDays}
              input={lookbackWindowDays}
              label="Lookback window"
              min={21}
              step={1}
              suffix="days"
              type="number"
            />
          </ErrorTooltip>
          <div className={classNames(gridCss.span12)}>
            <LabeledInput
              description="Like tax brackets: as the merchant's actual return rate climbs past each threshold, the credit rate for that band steps down. Each row covers actual return rates up to its threshold, paid out at the listed credit rate. Thresholds must climb row-to-row; rates must not. Leave the last row's threshold blank to make it unbounded — that rate applies to everything above the previous row."
              label="Credit Rate Brackets"
            >
              <div className={classNames(gridCss.grid, gridCss.span12)}>
                <div className={classNames(gridCss.span6)}>
                  <Text fontSize="xs" fontWeight="medium">
                    Threshold (return rate)
                  </Text>
                </div>
                <div className={classNames(gridCss.span5)}>
                  <Text fontSize="xs" fontWeight="medium">
                    Credit rate
                  </Text>
                </div>
                <div className={classNames(gridCss.span1)} />
              </div>
              <IterableMap items={tiers.inputs} keyFn={(_, i) => i}>
                {(tierInput: DynamicCreditsTierForm, index: number) => {
                  const isLast = index === tiers.value.length - 1;
                  const { thresholdRate, rate } = tierInput.inputs;
                  const tierFieldErrors = validation.fieldErrors.tiers[index];
                  return (
                    <div
                      className={classNames(gridCss.grid, gridCss.span12)}
                      style={{ alignItems: "center" }}
                    >
                      <ErrorTooltip
                        className={classNames(gridCss.span6)}
                        error={tierFieldErrors?.thresholdRate ?? null}
                      >
                        <FormTextInput
                          allowInvalidNumbers
                          description=""
                          disabled={!pricedReturnRateValid}
                          errorOverride={!!tierFieldErrors?.thresholdRate}
                          input={thresholdRate}
                          label=""
                          max={100}
                          min={0}
                          onPaste={(e) =>
                            handleTierPaste(e, index, "thresholdRate")
                          }
                          placeholder={
                            !pricedReturnRateValid
                              ? "Set priced return rate first"
                              : isLast
                                ? "Unbounded"
                                : ""
                          }
                          step={0.01}
                          suffix="%"
                          type="number"
                        />
                      </ErrorTooltip>
                      <ErrorTooltip
                        className={classNames(gridCss.span5)}
                        error={tierFieldErrors?.rate ?? null}
                      >
                        <FormTextInput
                          allowInvalidNumbers
                          description=""
                          errorOverride={!!tierFieldErrors?.rate}
                          input={rate}
                          label=""
                          max={100}
                          min={0}
                          onPaste={(e) => handleTierPaste(e, index, "rate")}
                          step={0.01}
                          suffix="%"
                          type="number"
                        />
                      </ErrorTooltip>
                      <Flex
                        align="center"
                        className={classNames(gridCss.span1)}
                        justify="center"
                      >
                        <IconButton
                          disabled={tiers.value.length === 1}
                          onClick={() => removeTier(index)}
                          size={ButtonSize.SMALL}
                          theme={IconButtonTheme.DANGER}
                        >
                          <TrashIcon style={{ width: 12, height: 12 }} />
                        </IconButton>
                      </Flex>
                    </div>
                  );
                }}
              </IterableMap>
              <Flex justify="flex-end" style={{ marginTop: "8px" }}>
                <RedoButton
                  hierarchy="secondary"
                  IconLeading={PlusIcon}
                  onClick={addTier}
                  size="xs"
                  text="Add bracket"
                />
              </Flex>
              {validation.fieldErrors.form && (
                <Text
                  fontSize="xs"
                  style={{ marginTop: "8px" }}
                  textColor="error"
                >
                  {validation.fieldErrors.form}
                </Text>
              )}
            </LabeledInput>
          </div>
        </>
      )}
    </>
  );
});

export const FinalSaleReturnsCard = memo(function FinalSaleReturnsCard({
  input,
}: {
  input: FinalSaleReturnsForm;
}) {
  const {
    enabled,
    validCollections,
    validCollectionTitles,
    validProductTags,
    reimbursementPercentage,
    shippingLabelCoveragePrice,
    finalSaleRevSharePercentage,
    eligibleDiscountThreshold,
    allEligible,
    dynamicCredits,
  } = input.inputs;

  const team = useContext(TeamContext);

  return (
    <section className={gridCss.grid}>
      <div className={gridCss.span12}>
        <FormSwitch input={enabled} label="Coverage Enabled">
          Enable customers to buy coverage
        </FormSwitch>
      </div>
      <div className={gridCss.span12}>
        <FormChipInput
          delimiter={ChipDelimiter.NEWLINE}
          description="Coverage will be added for these Shopify order tags (e.g. wholesale)."
          input={validProductTags}
          label="Included order tags"
          trimWhitespace
        />
      </div>
      <div className={gridCss.span12}>
        <LabeledInput
          description="Coverage will be offered for these Shopify collections. The first 100 collections are shown by default. To access more, search by title."
          label="Included collections"
        >
          <CollectionAutocomplete
            keyFn={(collection) => collection.handle}
            setValue={validCollections.setValue}
            setValueTitles={validCollectionTitles.setValue}
            value={validCollections.value}
          />
        </LabeledInput>
      </div>
      <div className={classNames(gridCss.span12)}>
        <FormTextInput
          description="Items discounted above this threshold become eligible for final sale coverage."
          input={eligibleDiscountThreshold}
          label="Eligible discount threshold"
          max={100}
          min={0}
          onKeyDown={(e) => {
            if (
              e.key === "Backspace" &&
              eligibleDiscountThreshold.value.length === 1
            ) {
              eligibleDiscountThreshold.setValue("");
            }
          }}
          prefix="%"
          step={0.01}
          type="number"
        />
      </div>
      <div className={classNames(gridCss.span6)}>
        <FormTextInput
          description="Percentage of the purchase price to reimburse for final sale items (0-100%)"
          input={reimbursementPercentage}
          label="Reimbursement Percentage"
          max={100}
          min={0.01}
          step={0.01}
          suffix="%"
        />
      </div>
      <div className={classNames(gridCss.span12)}>
        <FormSwitch input={allEligible} label="All Eligible">
          Make all products eligible for final sale returns
        </FormSwitch>
      </div>
      <DynamicCreditsSection input={dynamicCredits} />
      <div className={classNames(gridCss.span12)}>
        <Divider />
      </div>
      <div className={classNames(gridCss.span12)}>
        <Flex align="center" flexDirection="row" gap="sm">
          <strong>Pricing</strong>
          <Tooltip
            arrow
            placement="bottom"
            title={
              <Text>
                There are two components to final sale returns pricing:
                <br />
                1. The dynamic pricing, which is usually used to cover the cost
                of the item in case it is returned.
                <br />
                2. The shipping label coverage price, which covers the cost of
                the shipping label for the return.
                <br />
                These prices are added together to determine the total price
                that customers pay for coverage. Usually the shipping label
                coverage price is equal to the returns coverage price. Dynamic
                pricing is added to the shipping label coverage price to
                determine the total price that customers pay for coverage. For
                mixed carts (normal return coverage + final sale coverage) we
                use whichever price is higher to cover the cart. This should
                always be the final sale coverage price, since it includes a
                dynamic cart value component. The cost to cover the other items
                for returns will be included in the price collected for final
                sale.
              </Text>
            }
          >
            <Flex>
              <HelpIcon style={{ width: "16px", height: "16px" }} />
            </Flex>
          </Tooltip>
        </Flex>
      </div>

      <div className={classNames(gridCss.span12)}>
        <FormTextInput
          description="Percentage of final sale revenue that the merchant keeps (0-100%)"
          input={finalSaleRevSharePercentage}
          label="Rev Share Percentage"
          max={100}
          min={0}
          prefix="%"
          step={0.01}
          textAlign="left"
          type="number"
        />
      </div>
      <div className={classNames(gridCss.span12)}>
        <FormTextInput
          description="Fixed price to cover shipping label costs for final sale returns"
          input={shippingLabelCoveragePrice}
          label="Shipping Label Coverage Price"
          min={0}
          prefix={getCurrencySymbol(team?._shopify.currency || Currency.USD)}
          step={0.01}
          suffix={
            team?._shopify.currency === "USD" ? "" : team?._shopify.currency
          }
          type="number"
        />
      </div>
      <div className={gridCss.span12}>
        <Pricing
          adminApp
          canEditSettings
          currencyDisplay={getCurrencySymbol(
            team?._shopify.currency || Currency.USD,
          )}
          input={input}
        />
        <div style={{ marginTop: "16px" }}>
          <InternationalPriceRoundingButton />
        </div>
      </div>
    </section>
  );
});
